import { useCallback, useEffect, useState } from 'react';

import { useClient } from 'urql';

import { useAppState, useFunctionPolling } from '@sg/garnish';
import { OrderState, OrderType } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsBagOpen } from '../useBagNavigation/useBagNavigation';
import { CartDocument, type CartQuery } from '../useCart/GraphQL/Cart.generated';
import { useCart } from '../useCart/useCart';
import {
  GroupOrderPostOrderDocument,
  type GroupOrderPostOrderQuery,
} from './GraphQL/GroupOrderPostOrder.generated';
import {
  checkIfActiveGroupOrderHost,
  checkIfGroupOrderDismissed,
  getActiveGroupOrderId,
  storeActiveGroupOrder,
  storeDismissedGroupOrderId,
} from './group-order-reference-storage';
import { useResetGroupOrderingState } from './useResetGroupOrderingState';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Synchronizes active group-order state by polling while the app is active and
 * outside Bag, then clears stale frontend state when the order ends or disappears.
 */
export const useGroupOrderingState = () => {
  const client = useClient();
  const { cart } = useCart();
  const isBagOpen = useIsBagOpen();

  const resetGroupOrderingState = useResetGroupOrderingState();

  const pollingIntervalSeconds = useFeatureFlag('CELS-5826-order-group-order-polling-interval-seconds'); // prettier-ignore

  // ─── State ────────────────────────────────────────────────────────────────

  const [terminalGroupOrder, setTerminalGroupOrder] = useState<TerminalGroupOrder>();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const pollingInterval = pollingIntervalSeconds * MILLISECONDS_PER_SECOND;

  const isGroupOrder = Boolean(cart?.multipartyDetails);
  const isHost = Boolean(cart?.currentUserOrderPermissions.canSubmitOrder);
  const isGroupOrderFinished = checkIfGroupOrderFinished(cart);
  const isCurrentGroupOrderActive = isGroupOrder && !isGroupOrderFinished;
  const currentGroupOrderId = isGroupOrder ? cart?.id : undefined;
  const storedActiveGroupOrderId = getActiveGroupOrderId();
  const groupOrderId = currentGroupOrderId ?? storedActiveGroupOrderId;

  const shouldCheckTerminalGroupOrder = Boolean(groupOrderId && !isCurrentGroupOrderActive);
  const isPollingEnabled = isCurrentGroupOrderActive && !isBagOpen && pollingIntervalSeconds > 0; // prettier-ignore

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleTerminalGroupOrder = useCallback(
    async (orderId: string) => {
      const isHostOrder = checkIfActiveGroupOrderHost(orderId);

      if (isHostOrder) {
        resetGroupOrderingState();
        storeDismissedGroupOrderId(orderId);

        return;
      }

      const response = await client
        .query<GroupOrderPostOrderQuery>(
          GroupOrderPostOrderDocument,
          { orderId },
          { requestPolicy: 'network-only' },
        )
        .toPromise();

      resetGroupOrderingState();

      const terminalOrder = getTerminalGroupOrder(response.data?.order);
      const hasDismissedOrder = terminalOrder
        ? checkIfGroupOrderDismissed(terminalOrder.id)
        : false;

      if (!terminalOrder || hasDismissedOrder) return;

      setTerminalGroupOrder(terminalOrder);
    },
    [client, resetGroupOrderingState],
  );

  const handlePoll = useCallback(async () => {
    if (!isPollingEnabled || !currentGroupOrderId) return;

    await client.query<CartQuery>(CartDocument, {}, { requestPolicy: 'network-only' }).toPromise();
  }, [client, currentGroupOrderId, isPollingEnabled]);

  const dismissTerminalGroupOrder = useCallback(() => {
    if (!terminalGroupOrder) return;

    storeDismissedGroupOrderId(terminalGroupOrder.id);
    setTerminalGroupOrder(undefined);
  }, [terminalGroupOrder]);

  const handleActiveGroupOrderChange = useCallback(() => {
    if (!currentGroupOrderId || !isCurrentGroupOrderActive) return;

    storeActiveGroupOrder({ orderId: currentGroupOrderId, isHost });
  }, [currentGroupOrderId, isCurrentGroupOrderActive, isHost]);

  // ─── App State ─────────────────────────────────────────────────────────────

  const appState = useAppState({
    pause: !isPollingEnabled && !shouldCheckTerminalGroupOrder,
    onVisible: handlePoll,
  });

  const shouldSkipPolling = !isPollingEnabled || appState !== 'active';

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleTerminalGroupOrderChange = useCallback(() => {
    if (!shouldCheckTerminalGroupOrder || !groupOrderId || appState !== 'active') return;

    void handleTerminalGroupOrder(groupOrderId);
  }, [appState, groupOrderId, handleTerminalGroupOrder, shouldCheckTerminalGroupOrder]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(handleActiveGroupOrderChange, [handleActiveGroupOrderChange]);
  useEffect(handleTerminalGroupOrderChange, [handleTerminalGroupOrderChange]);

  useFunctionPolling(pollingInterval, handlePoll, shouldSkipPolling);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    terminalGroupOrder,
    dismissTerminalGroupOrder,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfGroupOrderFinished(cart: CartQuery['cart'] | undefined) {
  return (
    !cart || cart.orderState === OrderState.Complete || cart.orderState === OrderState.Discarded
  );
}

function getTerminalGroupOrder(order: GroupOrderPostOrderQuery['order'] | undefined) {
  const hostDisplayName = order?.multipartyDetails?.displayName;
  const isTerminalOrder =
    order?.orderState === OrderState.Complete || order?.orderState === OrderState.Discarded;

  if (!order || !isTerminalOrder) return;

  return {
    id: order.id,
    hasCurrentUserItems: order.lineItems.some((lineItem) => lineItem.orderParticipant?.isMe),
    hostDisplayName,
    orderChannel: getOrderChannel(order),
    orderState: order.orderState,
  };
}

function getOrderChannel(order: GroupOrderPostOrderQuery['order']): OrderChannel {
  if (order.restaurant?.isOutpost) return 'outpost';
  if (order.orderType === OrderType.Delivery) return 'delivery';

  return 'pickup';
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MILLISECONDS_PER_SECOND = 1000;

// ─── Types ───────────────────────────────────────────────────────────────────

type TerminalGroupOrder = {
  id: string;
  hasCurrentUserItems: boolean;
  hostDisplayName: string | undefined;
  orderChannel: OrderChannel;
  orderState: OrderState.Complete | OrderState.Discarded;
};

type OrderChannel = 'delivery' | 'outpost' | 'pickup';
